import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { auth, db } from './protected/firebase'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { useAuth } from '../../contexts/AuthContext'
import mailIcon from '../../assets/icons/mail.png';
import passwordIcon from '../../assets/icons/padlock.png';
import showIcon from '../../assets/icons/unhide.png';
import hideIcon from '../../assets/icons/hide.png';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, user, updateRememberMe, clearRememberMe } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user?.emailVerified) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const container = document.getElementById('circle-container');
    if (!container) return;
    container.innerHTML = '';

    const ellipses = [
      { width: '30vw', height: '30vw', left: '70vw', top: '5vh', background: 'linear-gradient(200.63deg, #CCE307 25.75%, #8946EA 77.76%)' },
      { width: '18vw', height: '18vw', left: '40vw', top: '8vh', background: 'linear-gradient(208.25deg, #CCE307 16.24%, #8946EA 36.77%)', transform: 'matrix(-1, 0, 0, 1, 0, 0)' },
      { width: '28vw', height: '28vw', left: '5vw', top: '45vh', background: 'linear-gradient(219.99deg, #CCE307 32.87%, #8146E2 68.9%)' },
      { width: '12vw', height: '12vw', left: '55vw', top: '70vh', background: 'linear-gradient(191.73deg, #CCE307 12.04%, #8946EA 75.7%)' }
    ];

    ellipses.forEach(({ width, height, left, top, background, transform }) => {
      const circle = document.createElement('div');
      Object.assign(circle.style, {
        position: 'absolute', width, height, left, top,
        background, borderRadius: '50%', zIndex: 0,
        pointerEvents: 'none', transform: transform || ''
      });
      container.appendChild(circle);
    });

    const rememberedEmail = localStorage.getItem('cashmate-remember-me');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      await user.reload();

      if (!user.emailVerified) {
        setError('Please verify your email address before logging in. Check your inbox including "spam" for the verification email.');
        return;
      }

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User account not found. Please sign up first.');
      }

      await updateDoc(userDocRef, {
        lastLogin: new Date().toISOString(),
        emailVerified: true
      });

      formData.rememberMe ? updateRememberMe(user.uid) : clearRememberMe();

      navigate('/dashboard', { replace: true });
      window.location.reload();
    } catch (err) {
      let errorMessage = 'Failed to login. Please try again.';
      
      // Map Firebase error codes to user-friendly messages
      switch (err.code) {
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email. Please sign up first.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again or reset your password.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later or reset your password.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid credentials. Please try again or reset your password.';
          break;
        default:
          // If it's a custom error message, use it, otherwise use the default
          errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05060f] p-4 sm:p-6 lg:p-8 relative overflow-hidden" dir="ltr">
      <div id="circle-container" className="absolute inset-0 overflow-hidden z-0" />

      <div className="w-full relative z-10 max-w-[1000px] mx-auto">
        <div className="backdrop-blur-2xl rounded-[20px] sm:rounded-[32px] flex flex-col md:flex-row overflow-hidden shadow-xl border border-white/20" style={{ background: 'linear-gradient(to bottom right, rgba(20, 20, 20, 0.2), rgba(40, 40, 40, 0.1))' }}>
          {/* left side */}
          <div className="w-full md:w-1/3 p-6 sm:p-8 flex flex-col items-start border-b md:border-b-0 md:border-r border-white/40 relative" style={{ borderRadius: '15px', background: 'linear-gradient(200.06deg, rgba(255, 255, 255, 0.8) 10.98%, rgba(255, 255, 255, 0.1) 95.69%)' }}>
            <h2 className="font-extrabold text-black leading-snug relative mt-6 sm:mt-8 md:mt-5 hidden md:block" style={{ fontFamily: 'Century Gothic', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', letterSpacing: '0.05em' }}>LOGIN
              <div className="absolute left-0" style={{ bottom: '-0.6rem', width: 'clamp(40px, 8vw, 100px)', height: 'clamp(2px, 0.5vw, 4px)', backgroundColor: 'black' }} />
            </h2>

            <h2 className="font-extrabold text-black leading-snug relative mt-6 sm:mt-8 md:mt-5 block md:hidden" style={{ fontFamily: 'Century Gothic', fontSize: 'clamp(1.5rem, 7vw, 2rem)', letterSpacing: '0.05em' }}>LOGIN
              <div className="absolute left-0" style={{ bottom: '-0.6rem', width: 'clamp(40px, 10vw, 80px)', height: 'clamp(2px, 0.5vw, 4px)', backgroundColor: 'black' }} />
            </h2>

            <Link to="/signup">
              <button
                className="
                  text-black font-bold py-3 md:py-3 lg:py-4 px-8 md:px-10 lg:px-12 rounded-full transition-all whitespace-nowrap
                  w-full md:w-auto text-sm md:text-sm lg:text-base
                  relative mt-6 md:mt-6
                  md:absolute md:left-1/2 md:transform md:-translate-x-1/2 md:bottom-28 /* Desktop: lower near bottom */
                "
                style={{
                  background: 'linear-gradient(100.37deg, rgba(255, 255, 255, 0.35) 44.59%, rgba(153, 153, 153, 0.35) 102.2%)',
                  border: '2px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '-10px 4px 20px rgba(0, 0, 0, 0.15)',
                  borderRadius: '65px',
                  fontFamily: 'Century Gothic',
                  letterSpacing: '0.1em'
                }}
              >
                CREATE ACCOUNT
              </button>
            </Link>

          </div>

          {/* right side */}
          <div className="md:w-2/3 p-8 lg:p-12 flex flex-col justify-center" style={{ borderRadius: '20px' }}>
            <div className="flex items-center mb-8 justify-center md:justify-start">
              <img src="/images/logo.png" alt="Cashmate Logo" style={{ width: 'clamp(60px, 8vw, 94px)', height: 'clamp(60px, 8vw, 94px)', marginRight: 'clamp(10px, 3vw, 20px)' }} />
              <h3 className="font-normal text-white" style={{ fontFamily: 'Century Gothic', fontWeight: 340, fontSize: 'clamp(24px, 6vw, 34px)', lineHeight: 'clamp(30px, 7vw, 40px)', letterSpacing: '0.3em', textAlign: 'center' }}>CASHMATE</h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 flex flex-col items-center">
              {['email', 'password'].map((field, index) => {
                const isPasswordField = field === 'password';
                const show = isPasswordField ? showPassword : false;

                return (
                  <div key={index} className="w-full flex justify-center">
                    <div className="relative w-[90%] max-w-[500px] mobile-input">
                      <div className="absolute inset-y-0 left-2 md:left-3 flex items-center pointer-events-none">
                        {field === 'email' && <img src={mailIcon} alt="Mail Icon" className="w-6 h-6" />}
                        {isPasswordField && <img src={passwordIcon} alt="Password Icon" className="w-6 h-6" />}
                      </div>

                      {isPasswordField && (
                        <button type="button" onClick={() => setShowPassword(prev => !prev)} className="absolute inset-y-0 right-2 md:right-3 flex items-center">
                          <img src={showPassword ? hideIcon : showIcon} alt="Toggle visibility" className="w-6 h-6" />
                        </button>
                      )}

                      <input
                        id={field}
                        name={field}
                        type={field === 'email' ? 'text' : show ? 'text' : 'password'}
                        required
                        className="w-full text-white rounded-full border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:bg-gray-500 placeholder:text-black text-sm md:text-base py-2 md:py-3 px-10 md:px-14"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.35)',
                          fontFamily: 'Century Gothic',
                          letterSpacing: '0.1em',
                          paddingLeft: '2.5rem',
                          paddingRight: '2.5rem',
                          '@media (min-width: 768px)': {
                            paddingLeft: '3.5rem',
                            paddingRight: '3.5rem',
                          }
                        }}
                        placeholder={field === 'email' ? 'E-MAIL' : 'PASSWORD'}
                        value={formData[field]}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                );
              })}

              <div className="w-[90%] max-w-[500px] flex justify-between items-center">
                <div className="flex items-center">
                  <input id="rememberMe" name="rememberMe" type="checkbox" checked={formData.rememberMe} onChange={handleChange} className="h-4 w-4 text-purple-600 border-gray-300 rounded" style={{ fontFamily: 'Century Gothic', letterSpacing: '0.1em' }}  />
                  <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-300" style={{ fontFamily: 'Century Gothic', letterSpacing: '0.1em' }}>Remember me</label>
                </div>
                <Link to="/forgot-password" className="text-xs md:text-sm text-purple-400 hover:text-purple-300 ml-4 whitespace-nowrap" style={{ fontFamily: 'Century Gothic', letterSpacing: '0.1em' }}>FORGOT PASSWORD?</Link>
              </div>

              {error && <p className="text-red-400 text-sm text-center">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-[80%] sm:max-w-[500px] py-2 px-4 rounded-full text-white bg-[#392954] hover:bg-[#392954]/80 backdrop-blur-md hover:scale-105 transition-all duration-300 font-['Century_Gothic'] text-sm md:text-base md:py-3"
                style={{
                  letterSpacing: '0.3em',
                }}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  "ENTER"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
